import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import DiscordAuthService from '../../services/DiscordAuthService';
import PlayerService from '../../services/PlayerService';

const MinecraftCallback = () => {
    const location = useLocation();
    const [status, setStatus] = useState('loading');
    const [error, setError] = useState('');

    useEffect(() => {
        const handleCallback = async () => {
            try {
                // Get the authorization code from URL
                const urlParams = new URLSearchParams(location.search);
                const code = urlParams.get('code');

                if (!code) {
                    throw new Error('No authorization code received');
                }

                console.log('Received authorization code:', code);

                try {
                    // Exchange code for Discord user data
                    console.log('Exchanging code for Discord user data...');
                    const userData = await DiscordAuthService.getDiscordUserData(code, 'minecraft');
                    
                    if (!userData || !userData.id) {
                        throw new Error('Failed to get Discord user data');
                    }

                    console.log('Received Discord user data:', {
                        id: userData.id,
                        username: userData.username,
                        avatar: userData.avatar
                    });

                    // Ensure player exists in Firebase
                    console.log('Creating/finding player in Firebase...');
                    const playerResult = await PlayerService.findOrCreatePlayerByDiscord({
                        discordId: userData.id,
                        discordUsername: userData.username,
                        avatar: userData.avatar
                    });
                    console.log('Player data result:', playerResult);

                    // Verify player was created/found
                    const verifyPlayer = await PlayerService.getPlayerData(userData.id);
                    console.log('Verified player data:', verifyPlayer);

                    if (!verifyPlayer?.data) {
                        throw new Error('Failed to create/verify player in Firebase');
                    }

                    // Find pending Minecraft link
                    console.log('Finding pending Minecraft link...');
                    const minecraftUuid = await PlayerService.findPendingMinecraftLink();
                    if (!minecraftUuid) {
                        throw new Error('No pending Minecraft link found');
                    }

                    console.log('Found pending link for Minecraft UUID:', minecraftUuid);

                    // Link the accounts
                    console.log('Attempting to link accounts...');
                    const success = await PlayerService.linkMinecraftAccount(userData.id, minecraftUuid);
                    
                    if (!success) {
                        // Verify if link exists
                        console.log('Link attempt unsuccessful, verifying existing link...');
                        const existingLink = await PlayerService.getMinecraftLink(minecraftUuid);
                        console.log('Existing link check result:', existingLink);
                        
                        if (existingLink === userData.id) {
                            // Already linked to this Discord ID, consider it a success
                            console.log('Minecraft account already linked to this Discord ID');
                            setStatus('success');
                        } else {
                            throw new Error(`Failed to link Minecraft account - possible duplicate link attempt. Existing link: ${existingLink}`);
                        }
                    } else {
                        // Verify the link was created
                        console.log('Link successful, verifying link creation...');
                        const verifyLink = await PlayerService.getMinecraftLink(minecraftUuid);
                        console.log('Link verification result:', verifyLink);
                        
                        if (verifyLink !== userData.id) {
                            throw new Error(`Link verification failed. Expected: ${userData.id}, Got: ${verifyLink}`);
                        }
                        
                        console.log('Successfully linked and verified Minecraft account');
                        setStatus('success');
                    }
                } catch (error) {
                    console.error('Detailed error in linking process:', {
                        message: error.message,
                        stack: error.stack,
                        error
                    });
                    throw error; // Re-throw to be caught by outer catch
                }

            } catch (error) {
                console.error('Error during Minecraft callback:', error);
                setStatus('error');
                setError(error.message || 'An unexpected error occurred');
            }
        };

        handleCallback();
    }, [location]);

    const renderContent = () => {
        switch (status) {
            case 'success':
                return (
                    <>
                        <h2 style={{ marginBottom: '1rem', color: '#00ff00' }}>Success!</h2>
                        <p style={{ marginBottom: '1rem' }}>Your Minecraft account has been successfully linked.</p>
                        <p>You can now return to Minecraft.</p>
                    </>
                );
            case 'error':
                return (
                    <>
                        <h2 style={{ marginBottom: '1rem', color: '#ff0000' }}>Error</h2>
                        <p style={{ marginBottom: '1rem' }}>{error}</p>
                        <p>Please try again in Minecraft.</p>
                    </>
                );
            default:
                return (
                    <>
                        <h2 style={{ marginBottom: '1rem' }}>Linking Minecraft Account</h2>
                        <p style={{ marginBottom: '1rem' }}>Please wait while we connect your accounts...</p>
                        <div style={{
                            width: '50px',
                            height: '50px',
                            border: '3px solid #00ff00',
                            borderTopColor: 'transparent',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                            margin: '0 auto'
                        }} />
                    </>
                );
        }
    };

    return (
        <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            backgroundColor: '#1a1a1a',
            color: '#ffffff',
            fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif'
        }}>
            <div style={{
                textAlign: 'center',
                padding: '2rem',
                borderRadius: '8px',
                backgroundColor: '#2a2a2a'
            }}>
                {renderContent()}
                <style>
                    {`
                        @keyframes spin {
                            0% { transform: rotate(0deg); }
                            100% { transform: rotate(360deg); }
                        }
                    `}
                </style>
            </div>
        </div>
    );
};

export default MinecraftCallback;